import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Circle, RotateCcw, Search, Loader2,
  ListChecks, Clock, Building2, Repeat, Calendar as CalendarIcon, User as UserIcon,
  MessageSquarePlus, Eye,
} from 'lucide-react';
import { formatRomeDateTime, formatRomeDate } from '../lib/datetime';

const frequencyLabels = {
  daily: 'Giornaliera', weekly: 'Settimanale', biweekly: 'Bisettimanale',
  monthly: 'Mensile', quarterly: 'Trimestrale', semiannual: 'Semestrale', yearly: 'Annuale'
};

const ScheduledMaintenanceDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [hotelName, setHotelName] = useState('');
  const [technicianName, setTechnicianName] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todo'); // todo | done | all
  const [search, setSearch] = useState('');
  const [noteItem, setNoteItem] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [acting, setActing] = useState(false);

  const role = user?.role;
  const assignedIds = m?.assigned_to_ids && m.assigned_to_ids.length > 0
    ? m.assigned_to_ids
    : (m?.assigned_to ? [m.assigned_to] : []);
  const isAssignee = user?.id && assignedIds.includes(user.id);
  const isManager = role === 'super_admin' || role === 'hotel_manager';
  const canComplete = isAssignee || role === 'super_admin';
  const canUncomplete = isManager;
  const isReadOnly = role === 'technician' && !isAssignee;

  const fetchMaintenance = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/scheduled-maintenance/${id}`);
      setM(res.data);
      // Hotel name
      try {
        const h = await api.get('/hotels');
        const found = (h.data || []).find(x => x.id === res.data.hotel_id);
        setHotelName(found?.name || '—');
      } catch (_) {}
      // Tecnici assegnati (multi)
      const ids = res.data.assigned_to_ids && res.data.assigned_to_ids.length > 0
        ? res.data.assigned_to_ids
        : (res.data.assigned_to ? [res.data.assigned_to] : []);
      if (ids.length > 0) {
        try {
          const u = await api.get('/users');
          const names = ids
            .map(uid => (u.data || []).find(x => x.id === uid)?.name)
            .filter(Boolean);
          setTechnicianName(names.join(', ') || '—');
        } catch (_) { setTechnicianName('—'); }
      } else {
        setTechnicianName('');
      }
    } catch (e) {
      toast.error('Errore nel caricamento manutenzione');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMaintenance(); }, [id]);

  const completeItem = async (itemId) => {
    if (acting) return;
    if (!canComplete) {
      toast.error('Solo il tecnico assegnato puo\' completare gli elementi');
      return;
    }
    setActing(true);
    try {
      const res = await api.post(`/scheduled-maintenance/${id}/items/${itemId}/complete`);
      setM(res.data.maintenance);
      if (res.data.next_occurrence) {
        toast.success('Checklist completata! Nuova occorrenza creata automaticamente');
      } else {
        toast.success('Elemento completato');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setActing(false); }
  };

  const uncompleteItem = async (itemId) => {
    if (acting) return;
    if (!canUncomplete) {
      toast.error('Solo manager / super admin possono annullare un completamento');
      return;
    }
    if (!window.confirm('Vuoi annullare il completamento di questo elemento?')) return;
    setActing(true);
    try {
      const res = await api.post(`/scheduled-maintenance/${id}/items/${itemId}/uncomplete`);
      setM(res.data);
      toast.success('Completamento annullato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setActing(false); }
  };

  const saveNote = async () => {
    if (!noteItem) return;
    try {
      const res = await api.patch(`/scheduled-maintenance/${id}/items/${noteItem.id}/note`, { note: noteText });
      setM(res.data);
      toast.success('Nota salvata');
      setNoteItem(null);
      setNoteText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const filteredItems = useMemo(() => {
    if (!m?.checklist) return [];
    let items = m.checklist;
    if (filter === 'todo') items = items.filter(i => !i.done);
    else if (filter === 'done') items = items.filter(i => i.done);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(i => (i.label || '').toLowerCase().includes(q) || (i.group || '').toLowerCase().includes(q));
    }
    return items;
  }, [m, filter, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of filteredItems) {
      if (!map.has(it.group)) map.set(it.group, []);
      map.get(it.group).push(it);
    }
    return Array.from(map.entries());
  }, [filteredItems]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-900" /></div>;
  }
  if (!m) return null;

  const p = m.progress || { done: 0, total: 0, pct: 0 };
  const overdue = new Date(m.scheduled_date) < new Date() && p.pct < 100 && p.total > 0;

  return (
    <div className="space-y-6" data-testid="scheduled-detail-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate('/scheduled')} data-testid="back-to-scheduled">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {m.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
            <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {hotelName} · {m.area}</span>
            <span className="flex items-center gap-1"><CalendarIcon className="w-4 h-4" /> {formatRomeDate(m.scheduled_date)}</span>
            <Badge variant="secondary"><Repeat className="w-3 h-3 mr-1" /> {frequencyLabels[m.frequency]}</Badge>
            {technicianName && <span className="flex items-center gap-1"><UserIcon className="w-4 h-4" /> {technicianName}</span>}
            {overdue && <Badge className="bg-red-600 hover:bg-red-700">In ritardo</Badge>}
            {m.is_legacy && <Badge variant="outline" className="text-amber-700 border-amber-400">Vecchio formato</Badge>}
          </div>
        </div>
      </div>

      {isReadOnly && (
        <div
          className="rounded-md p-3 flex items-start gap-2 text-sm"
          style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#1e40af' }}
          data-testid="readonly-banner"
        >
          <Eye className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Sola lettura:</b> non sei tra i tecnici assegnati a questa manutenzione. Puoi visualizzare i dettagli ma non modificare la checklist.
          </div>
        </div>
      )}

      {/* Legacy: niente checklist */}
      {m.is_legacy || (p.total === 0) ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <ListChecks className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm">Questa manutenzione non ha una checklist (vecchio formato o "Manutenzione semplice").</p>
            <p className="text-xs text-slate-400 mt-1">Le manutenzioni con checklist tracciano camera per camera. Crea una nuova programmazione con target per usare il sistema completo.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Progress card */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Avanzamento</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className={`text-4xl font-bold tabular-nums ${p.pct === 100 ? 'text-emerald-600' : 'text-blue-700'}`} data-testid="progress-pct">
                      {p.pct}%
                    </span>
                    <span className="text-slate-500">
                      {p.done} su {p.total} completati · {p.remaining} rimanenti
                    </span>
                  </div>
                </div>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden mt-3">
                <div
                  className={`h-full transition-all ${p.pct === 100 ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${p.pct}%` }}
                  data-testid="progress-bar"
                />
              </div>
            </CardContent>
          </Card>

          {/* Filtri */}
          <Card>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <div className="flex gap-1 rounded-lg bg-slate-100 p-1" data-testid="filter-tabs">
                {[
                  { v: 'todo', label: `Da fare (${m.checklist.filter(i => !i.done).length})` },
                  { v: 'done', label: `Completati (${m.checklist.filter(i => i.done).length})` },
                  { v: 'all', label: `Tutti (${m.checklist.length})` },
                ].map(t => (
                  <button
                    key={t.v}
                    onClick={() => setFilter(t.v)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded transition ${filter === t.v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                    data-testid={`filter-${t.v}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca camera, area..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="checklist-search" />
              </div>
            </CardContent>
          </Card>

          {/* Checklist raggruppata */}
          {grouped.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-400 text-sm">Nessun elemento corrisponde ai filtri.</CardContent></Card>
          ) : (
            grouped.map(([groupName, items]) => (
              <Card key={groupName}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-bold uppercase tracking-wide text-slate-600 flex items-center gap-2">
                    {groupName}
                    <span className="text-xs text-slate-400 normal-case font-normal">
                      ({items.filter(i => i.done).length}/{items.length})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {items.map(it => (
                      <div
                        key={it.id}
                        className={`flex items-center gap-3 px-4 py-3 ${it.done ? 'bg-emerald-50/40' : 'hover:bg-slate-50'}`}
                        data-testid={`checklist-item-${it.id}`}
                      >
                        {it.done ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${it.done ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                            {it.label}
                          </div>
                          {it.done && (
                            <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5">
                              <UserIcon className="w-3 h-3" />
                              <span>{it.done_by_name}</span>
                              <span>·</span>
                              <Clock className="w-3 h-3" />
                              <span>{formatRomeDateTime(it.done_at)}</span>
                            </div>
                          )}
                          {it.note && (
                            <div className="text-xs text-slate-600 bg-slate-100 rounded px-2 py-1 mt-1 inline-block">
                              📝 {it.note}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-slate-400 hover:text-slate-700"
                            onClick={() => { setNoteItem(it); setNoteText(it.note || ''); }}
                            title="Aggiungi/modifica nota"
                            data-testid={`note-btn-${it.id}`}
                          >
                            <MessageSquarePlus className="w-4 h-4" />
                          </Button>
                          {it.done ? (
                            canUncomplete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => uncompleteItem(it.id)}
                                title="Annulla completamento (manager)"
                                data-testid={`uncomplete-btn-${it.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )
                          ) : (
                            canComplete && (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 h-8"
                                onClick={() => completeItem(it.id)}
                                disabled={acting}
                                data-testid={`complete-btn-${it.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Fatto
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}

      {/* Dialog nota */}
      <Dialog open={!!noteItem} onOpenChange={(o) => !o && setNoteItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nota — {noteItem?.label}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="es: rotto, sostituito · da controllare di nuovo · cliente presente..."
            rows={4}
            data-testid="note-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteItem(null)}>Annulla</Button>
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={saveNote} data-testid="save-note-btn">Salva nota</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledMaintenanceDetailPage;
